"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Check, Cpu, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";

const PROVIDER_BRAND_LABELS = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google",
  mistral: "Mistral",
  meta: "Meta",
  cohere: "Cohere",
  xai: "xAI",
  deepseek: "DeepSeek",
  openrouter: "OpenRouter",
};



function getModelDisplayName(name) {
  if (!name) return "-";
  return name;
}

function getProviderBrandLabel(model) {
  if (!model) return "Unknown";
  return model.brand || PROVIDER_BRAND_LABELS[(model.provider || "").toLowerCase()] || (model.provider || "Unknown");
}

function supportsVision(model) {
  const supportFlag = model?.support_vision ?? model?.supportVision ?? model?.supportsVision;
  if (supportFlag === undefined || supportFlag === null) return true;
  return Boolean(supportFlag);
}

export default function ModelSelectorDialog({
  open,
  onOpenChange,
  models,
  selectedModelId,
  recommendedModelId,
  onSelectModel,
}) {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      setSearch("");
    }
  }, [open]);

  const filteredModels = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return models;

    return models.filter((model) => {
      const name = (model?.name || "").toLowerCase();
      const provider = (model?.provider || "").toLowerCase();
      return name.includes(term) || provider.includes(term);
    });
  }, [models, search]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[94vw] max-w-5xl border border-white/20 bg-[#07070d] p-0 text-white">
        <DialogHeader className="border-b border-white/10 px-5 py-4">
          <DialogTitle className="flex items-center gap-2 text-white">
            <Cpu className="h-4 w-4 text-purple-300" />
            Choose Model
            <span className="text-xs font-normal text-gray-400">({models.length})</span>
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 pb-5 pt-4">
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search model by name or provider..."
              className="border-white/20 bg-black/50 pl-9 text-white placeholder-gray-500"
            />
          </div>

          <div className="grid max-h-[58vh] grid-cols-1 gap-2 overflow-y-auto pr-1 md:grid-cols-2">
            {filteredModels.map((model) => {
              const isSelected = selectedModelId === model.id;
              const isRecommended = recommendedModelId === model.id;
              const modelSupportsVision = supportsVision(model);

              return (
                <button
                  key={model.id}
                  type="button"
                  onClick={() => {
                    onSelectModel(model.id);
                    onOpenChange(false);
                  }}
                  className={`rounded-xl border p-3 text-left transition-all duration-200 ${
                    isSelected
                      ? "border-purple-500/50 bg-purple-600/20 shadow-lg shadow-purple-500/10"
                      : "border-white/10 bg-white/5 hover:border-white/30"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-white">{getModelDisplayName(model.name)}</p>
                      <p className="mt-1 text-xs text-gray-400">Provider: {getProviderBrandLabel(model)}</p>
                      {!modelSupportsVision && (
                        <p
                          className="mt-1 text-[11px] text-amber-200/90"
                          title="This model does not support image or file inputs."
                        >
                          Vision not supported
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {!modelSupportsVision && (
                        <span
                          className="rounded-full bg-amber-500/15 px-2 py-1 text-[10px] uppercase tracking-wide text-amber-200"
                          title="This model does not support image or file inputs."
                        >
                          No Vision
                        </span>
                      )}
                      {isSelected ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-1 text-[10px] uppercase tracking-wide text-gray-200">
                          <Check className="h-3 w-3" />
                          Selected
                        </span>
                      ) : (
                        <span className="rounded-full bg-white/10 px-2 py-1 text-[10px] uppercase tracking-wide text-gray-300">
                          {isRecommended ? "Recommended" : "Model"}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}

            {filteredModels.length === 0 && (
              <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-xs text-gray-400">
                No models match your search.
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}